import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useHttpClient, useErrorMonitor } from '@wix/fe-essentials-standalone';
import {
  useProfileContext,
  ACTIONS,
} from '@components/Contexts/ProfileContext';
import { ClientProject, ReviewData } from '@common/types';
import {
  getReviewDataFromProjects,
  getServicesFromCategories,
} from './ProjectsReviewsUtils';
import s from './ProjectsReviews.module.scss';
import { Box, Pagination } from '@wix/design-system';
import ReviewsWithPaging from '@components/Reviews/ReviewsWithPaging';
import { copyOfExploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { useBi } from '@components/Hooks/useBi';
import { UpdateProjectResponse } from '@wix/ambassador-marketplace-projects/types';
import { PROFILE_REVIEWS_PER_PAGE } from '@utils/constants';
import Long from 'long';

type ProjectsReviewsProps = {
  isMobileView: boolean;
  onPageChange: () => void;
};

export const providerTranslations = {
  viewReviewSubjectCTA: 'profile-page-review-project-link',
  replyAuthorTitle: 'profile-page-review-reply-title',
  noReviews: 'profile-page-reviews-section-no-reviews-text',
};

const ProjectsReviewsWithPaging: FC<ProjectsReviewsProps> = ({
  isMobileView,
  onPageChange,
}) => {
  const httpClient = useHttpClient();
  const errorMonitor = useErrorMonitor();
  const { sessionId } = useBiSessionContext();
  const logger = useBi();
  const { services, isMyProfile, entry, dispatch, partnerProfile } =
    useProfileContext();

  const [reviews, setReviews] = useState<ReviewData[]>(
    getReviewDataFromProjects(partnerProfile.projects),
  );
  const [isLoadingNewReviews, setIsLoadingNewReviews] =
    useState<boolean>(false);

  const [projectReviewsPageCount, setProjectReviewsPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchReviews = useCallback(async (page: number) => {
    setIsLoadingNewReviews(true);
    try {
      const { data } = await httpClient.post<{
        projects: Array<ClientProject>;
        projectsCursor?: string;
      }>('/api/partnerProjects', {
        partnerProfileId: partnerProfile.id,
        pagination: { page, limit: PROFILE_REVIEWS_PER_PAGE },
      });
      dispatch({
        type: ACTIONS.RESET_PROJECTS,
        ...data,
      });
    } catch (e) {
      errorMonitor.captureMessage(`Error @fetchPartnerProjects`, {
        contexts: {
          data: {
            partnerProfileId: partnerProfile.id,
            url: window.location.href,
          },
          error: { e },
        },
      });
    } finally {
      setIsLoadingNewReviews(false);
    }
  }, []);

  const fetchNextPartnerProjectsPage = useCallback(
    async (page: number) => {
      await fetchReviews(page);
    },
    [dispatch, errorMonitor, httpClient, partnerProfile],
  );

  const writeReplyToReview = useCallback(
    async (id: string, replyBody: string, revision?: Long) => {
      {
        const writeReplyReviewResponse =
          await httpClient.post<UpdateProjectResponse>(
            '/api/writeReplyReview',
            {
              reply: replyBody,
              id,
              revision,
            },
          );
        const updatedProject = writeReplyReviewResponse.data.project;
        dispatch({
          type: ACTIONS.UPDATE_PROJECT,
          project: updatedProject,
        });
      }
    },
    [dispatch, httpClient],
  );

  const logPaginationEvent = () => {
    logger.report(
      copyOfExploreResultsPageGenericClick({
        clickType: 'navigate between pages',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
      }),
    );
  };

  const serviceList = useMemo(
    () => getServicesFromCategories(services),
    [services],
  );

  const borderContainerClass = useMemo(() => {
    return isMobileView ? undefined : s.reviewsWithBorderContainer;
  }, [isMobileView]);

  useEffect(() => {
    setReviews(getReviewDataFromProjects(partnerProfile.projects));
  }, [partnerProfile]);

  useEffect(() => {
    (async () => {
      const numberOfPages =
        partnerProfile.providerRating.reviewsCount > 0
          ? Math.ceil(
              partnerProfile.providerRating.reviewsCount /
                PROFILE_REVIEWS_PER_PAGE,
            )
          : 0;
      setProjectReviewsPageCount(numberOfPages);
      await fetchReviews(currentPage);
    })();
  }, []);

  return (
    <>
      <ReviewsWithPaging
        borderContainerClass={borderContainerClass}
        isMobileView={isMobileView}
        i18nKeys={providerTranslations}
        services={serviceList}
        isMyProfile={isMyProfile}
        averageReviewRating={partnerProfile.providerRating.averageReviewRating}
        reviews={reviews}
        reviewsCount={partnerProfile.providerRating.reviewsCount}
        replyProps={{ writeReplyToReview }}
        marginBottom={isMobileView ? 'SP4' : 'SP6'}
        loadNewReview={isLoadingNewReviews}
      />
      {partnerProfile.providerRating?.reviewsCount > 0 && (
        <Box align="center" paddingTop="18px" margin="auto">
          <Pagination
            currentPage={currentPage}
            totalPages={projectReviewsPageCount}
            onChange={async ({ page, event }) => {
              event.preventDefault();
              onPageChange();
              setCurrentPage(page);
              await fetchNextPartnerProjectsPage(page);
              logPaginationEvent();
            }}
          />
        </Box>
      )}
    </>
  );
};

export default ProjectsReviewsWithPaging;
